import React, { useState } from "react";
import { ColumnDef, RowSelectionState } from "@tanstack/react-table";
import { PencilIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import Table from "../common/Table";
import RegionForm from "../forms/RegionForm";
import { useRegionsInfinite } from "../../hooks/useRegionsInfinite";
import {
  Region,
  RegionFormData,
  RegionUpdateFormData,
  RegionRequest,
} from "../../schemas/regionSchema";

const Regions: React.FC = () => {
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState<Region | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [search, setSearch] = useState("");

  const {
    regions,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    createRegion,
    updateRegion,
    deleteRegions,
  } = useRegionsInfinite({ search });

  const handleCreate = () => {
    setSelectedRegion(undefined);
    setIsFormModalOpen(true);
  };

  const handleEdit = (region: Region) => {
    setSelectedRegion(region);
    setIsFormModalOpen(true);
  };

  const handleDelete = async (region: Region) => {
    if (window.confirm(`Delete Region "${region.name}"?`)) {
      await deleteRegions([region.id]);
    }
  };

  const handleBulkDelete = (ids: string[]) => {
    if (
      window.confirm(`Are you sure you want to delete ${ids.length} region(s)?`)
    ) {
      deleteRegions(ids);
      setRowSelection({});
    }
  };

  const handleSubmit = async (data: RegionFormData | RegionUpdateFormData) => {
    setIsSubmitting(true);
    try {
      if (selectedRegion?.id) {
        await updateRegion({
          id: selectedRegion.id,
          region: data as RegionUpdateFormData,
        });
      } else {
        const payload: RegionRequest = {
          name: data.name || "",
          cityId: data.cityId || "",
        };
        await createRegion(payload);
      }
      setIsFormModalOpen(false);
      setSelectedRegion(undefined);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setIsFormModalOpen(false);
    setSelectedRegion(undefined);
  };

  const columns: ColumnDef<Region>[] = [
    { accessorKey: "name", header: "Name" },
    { accessorKey: "city.name", header: "City" },
  ];

  const actions = [
    {
      label: "Edit",
      icon: <PencilIcon className="h-4 w-4" />,
      onClick: handleEdit,
      variant: "success" as const,
    },
    {
      label: "Delete",
      icon: <TrashIcon className="h-4 w-4" />,
      onClick: handleDelete,
      variant: "danger" as const,
    },
  ];

  return (
    <>
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-xl font-bold text-gray-800">Regions</h1>
        <button
          onClick={handleCreate}
          className="p-2 bg-primary-default text-white rounded hover:bg-primary-light"
        >
          <PlusIcon className="h-5 w-5" />
        </button>
      </div>

      <Table
        columns={columns}
        data={regions as Region[]}
        actions={actions}
        isLoading={isLoading}
        maxHeight="48vh"
        pagination
        searchable
        infinite
        hasNextPage={hasNextPage}
        isFetchingMore={isFetchingNextPage}
        onLoadMore={() => fetchNextPage()}
        onSearch={setSearch}
        selectable
        getRowId={(region) => region.id}
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        bulkActions={[
          {
            label: "Delete Selected",
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: handleBulkDelete,
            variant: "danger" as const,
          },
        ]}
      />

      {isFormModalOpen && (
        <RegionForm
          isOpen={isFormModalOpen}
          onCancel={handleCancel}
          initialData={selectedRegion}
          onSubmit={handleSubmit}
          isLoading={isSubmitting}
          isUpdate={!!selectedRegion}
        />
      )}
    </>
  );
};

export default Regions;
